import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import PermissionsCell, { MIXED_PERMISSIONS } from './PermissionsCell';
import EMPTY_VALUE from '../../shared/emptyValue';

const renderCell = (props = {}) =>
  render(
    <ConfigProvider>
      <PermissionsCell {...props} />
    </ConfigProvider>,
  );

const trigger = () =>
  screen.getByRole('button', { name: /show permissions by account/i });

describe('management/Users PermissionsCell', () => {
  it('renders a placeholder when the user holds no role', () => {
    renderCell({ permissions: [] });

    expect(screen.getByText(EMPTY_VALUE)).toBeInTheDocument();
  });

  it('treats a missing list as empty', () => {
    renderCell();

    expect(screen.getByText(EMPTY_VALUE)).toBeInTheDocument();
  });

  describe('with one role', () => {
    it('names it', () => {
      renderCell({ permissions: ['Member'] });

      expect(screen.getByText('Member')).toBeInTheDocument();
    });

    it('does not collapse it to Mixed Permissions', () => {
      renderCell({ permissions: ['Member'] });

      expect(screen.queryByText(MIXED_PERMISSIONS)).not.toBeInTheDocument();
    });

    it('offers no expander', () => {
      renderCell({ permissions: ['Member'] });

      expect(
        screen.queryByRole('button', { name: /show permissions/i }),
      ).not.toBeInTheDocument();
    });
  });

  // Roles are per account, so someone working across accounts holds several.
  describe('with several roles', () => {
    const props = {
      permissions: ['Manager', 'Member'],
      permissionAssignments: [
        { account: 'Account Name B', permission: 'Manager' },
        { account: 'Account Name C', permission: 'Member' },
      ],
    };

    it('collapses them into one tag', () => {
      renderCell(props);

      expect(screen.getByText(MIXED_PERMISSIONS)).toBeInTheDocument();
      expect(screen.queryByText('Manager')).not.toBeInTheDocument();
    });

    // Unlike ACCOUNTS, the design puts no count on this cell.
    it('shows no count beside the tag', () => {
      renderCell(props);

      expect(screen.queryByText('2')).not.toBeInTheDocument();
    });

    // The breakdown is the point: a flat list of role names would not say which
    // role applies where.
    it('pairs each role with its account when expanded', async () => {
      renderCell(props);

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(screen.getByText('Manager')).toBeInTheDocument(),
      );
      expect(screen.getByText('Account Name B')).toBeInTheDocument();
      expect(screen.getByText('Member')).toBeInTheDocument();
      expect(screen.getByText('Account Name C')).toBeInTheDocument();
    });

    it('reports its expanded state', async () => {
      renderCell(props);
      expect(trigger()).toHaveAttribute('aria-expanded', 'false');

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(trigger()).toHaveAttribute('aria-expanded', 'true'),
      );
    });

    // Two accounts can grant the same role; both rows must still render.
    it('keeps duplicate role names as separate rows', async () => {
      renderCell({
        permissions: ['Manager', 'Member'],
        permissionAssignments: [
          { account: 'Account B', permission: 'Member' },
          { account: 'Account C', permission: 'Member' },
        ],
      });

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(screen.getAllByText('Member')).toHaveLength(2),
      );
    });
  });
});
